import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useCurrentTheme } from 'app/shared/theme';
import { Event } from 'app/shared/typings';
import { Box, Typography } from 'app/shared/components/atoms';
import { Accordion } from 'app/fan/components/molecules';
import { ReactComponent as Details } from 'icons/details.svg';

import { EventDetailsSectionDetails } from './EventDetailsSectionDetails';
import { useDynamicSections } from './useDynamicSections';

interface Props {
  event: Event;
}

export const EventCharacteristicsList: React.FC<
  React.PropsWithChildren<Props>
> = ({ event }) => {
  const { formatMessage } = useIntl();
  const { colors, dimensions } = useCurrentTheme();
  const { hash } = useLocation();

  const dynamicSections = useDynamicSections({
    isCompetition: !!event.competition?.id,
    isVenueConfirmed: event.isVenueConfirmed,
    venue: event.venue,
    seating: event.seating,
    propertyMap: event.propertyMap,
    isFilmed: event.isFilmed,
  });

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }
  }, [hash]);

  const sections = [
    {
      title: 'Event details',
      icon: Details,
      details: <EventDetailsSectionDetails />,
      openByDefault: false,
    },
    ...dynamicSections,
  ];

  return (
    <Box
      mb={0}
      mt={[2, 2, 6, 6]}
      px={dimensions.horizontalPadding.pageGridItem}
      id="event-characteristics"
    >
      <Typography
        fontSize="fontSize500"
        fontWeight="medium"
        color={colors.primary.white.base}
        fontFamily="title"
        as="h4"
        m={0}
        pb={3}
      >
        {formatMessage({
          id: 'event.eventCharacteristicsList.concertDetails',
        })}
      </Typography>
      <Accordion sections={sections} />
    </Box>
  );
};
